import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { endpoints, formatApiError } from "../lib/api";
import { fullSync } from "../lib/sync";
import { getAll, STORES } from "../lib/db";
import { Gear, UserPlus, X, CloudArrowUp, MapPin, FloppyDisk, Database, CircleNotch, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import { FRANCHISE_NAME } from "../lib/branding";
import AddressAutocomplete from "../components/AddressAutocomplete";

export default function Settings() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [storage, setStorage] = useState({});
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ email: "", name: "", role: "estimator", password: "" });
    const [msg, setMsg] = useState("");
    const [hq, setHq] = useState({ hq_address: "", hq_address_parts: null });
    const [hqDirty, setHqDirty] = useState(false);
    const [savingHq, setSavingHq] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);

    async function loadDemoData() {
        if (seeding) return;
        if (!window.confirm("Load the demo dataset? This adds 3 demo projects (with ROMs, P&L, Gantt phases, timesheets, schedule entries and shared proposals) plus a cleaning quote. It is additive — it never deletes your real data — and re-running just refreshes the demo records.")) return;
        setSeeding(true); setSeedResult(null); setMsg("");
        try {
            const { data } = await endpoints.seedDemoData();
            setSeedResult(data?.seeded || {});
            setMsg("Demo data loaded");
            try { await fullSync(); } catch { /* offline */ }
            setTimeout(() => setMsg(""), 4000);
        } catch (e) {
            setMsg(formatApiError(e));
        } finally {
            setSeeding(false);
        }
    }

    useEffect(() => {
        (async () => {
            if (user?.role === "admin") {
                try {
                    const { data } = await endpoints.listUsers();
                    setUsers(data || []);
                } catch {}
            }
            try {
                const { data } = await endpoints.franchiseSettings();
                setHq({ hq_address: data?.hq_address || "", hq_address_parts: data?.hq_address_parts || null });
            } catch {}
            const counts = {};
            for (const k of Object.values(STORES)) {
                try { counts[k] = (await getAll(k)).length; } catch { counts[k] = 0; }
            }
            setStorage(counts);
        })();
    }, [user]);

    async function saveHq() {
        setSavingHq(true);
        try {
            await endpoints.saveFranchiseSettings(hq);
            setMsg("HQ address saved");
            setHqDirty(false);
            setTimeout(() => setMsg(""), 3000);
        } catch (e) {
            setMsg(formatApiError(e));
        } finally {
            setSavingHq(false);
        }
    }

    async function createUser() {
        try {
            await endpoints.register(draft);
            const { data } = await endpoints.listUsers();
            setUsers(data || []);
            setOpen(false);
            setDraft({ email: "", name: "", role: "estimator", password: "" });
            setMsg("User created");
            setTimeout(() => setMsg(""), 3500);
        } catch (e) {
            setMsg(formatApiError(e));
        }
    }

    async function runSync() {
        const r = await fullSync();
        setMsg(r.pulled?.ok ? `Sync ok · ${r.pushed?.flushed || 0} pushed` : "Sync failed");
        setTimeout(() => setMsg(""), 4000);
    }

    const isAdmin = user?.role === "admin";

    return (
        <div className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Settings</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <Gear size={28} weight="bold" className="text-[var(--rom-orange)]" />
                        Franchise &amp; sync
                    </h1>
                </div>
                {msg && <span className="chip chip-green">{msg}</span>}
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="rom-card lg:col-span-2">
                    <div className="rom-card-head">
                        <h3>Users &amp; roles</h3>
                        {isAdmin && <button onClick={() => setOpen(true)} className="btn btn-primary btn-sm" data-testid="add-user">
                            <UserPlus size={12} weight="bold" /> Add user
                        </button>}
                    </div>
                    {isAdmin ? (
                        <table className="rom-table">
                            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th></tr></thead>
                            <tbody>
                                {users.map((u) => (
                                    <EditableUserRow key={u.id} u={u} onSaved={(next) => setUsers((us) => us.map((x) => x.id === u.id ? next : x))} />
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="rom-card-body text-sm text-[var(--rom-text-2)]">Admin access required to manage users.</div>
                    )}
                </div>

                <div className="rom-card">
                    <div className="rom-card-head"><h3>Sync &amp; local cache</h3></div>
                    <div className="rom-card-body space-y-3">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <KV k="Estimates" v={storage.jobs ?? 0} />
                            <KV k="Clients" v={storage.clients ?? 0} />
                            <KV k="Documents" v={storage.documents ?? 0} />
                            <KV k="Schedules" v={storage.rate_schedules ?? 0} />
                            <KV k="Queued ops" v={storage.sync_queue ?? 0} />
                        </div>
                        <button className="btn btn-secondary w-full" onClick={runSync} data-testid="settings-sync">
                            <CloudArrowUp size={14} weight="bold" /> Push &amp; pull now
                        </button>
                        <p className="text-[11px] text-[var(--rom-text-2)] leading-relaxed">
                            All data is cached in your browser's IndexedDB. The PWA works offline; changes queue locally until you reconnect.
                        </p>
                    </div>
                </div>
            </div>

            <div className="rom-card">
                <div className="rom-card-head">
                    <h3><MapPin size={14} weight="bold" /> Franchise HQ address</h3>
                    {hqDirty && (
                        <button onClick={saveHq} disabled={savingHq} className="btn btn-primary btn-sm" data-testid="hq-save">
                            <FloppyDisk size={12} weight="bold" /> {savingHq ? "Saving…" : "Save"}
                        </button>
                    )}
                </div>
                <div className="rom-card-body space-y-3">
                    <p className="text-xs text-[var(--rom-text-2)]">
                        This is the franchise's base of operations. Used to auto-detect <b>Far Job</b> on every ROM (when the site address is &gt;100 mi from here, mobilisation is bumped automatically).
                    </p>
                    {(user?.role === "admin" || user?.role === "manager") ? (
                        <>
                            <AddressAutocomplete
                                value={hq.hq_address}
                                onChange={(v) => { setHq((h) => ({ ...h, hq_address: v })); setHqDirty(true); }}
                                onPick={(parts) => {
                                    setHq({ hq_address: parts.full || parts.address, hq_address_parts: parts });
                                    setHqDirty(true);
                                }}
                                placeholder="Search the franchise HQ address…"
                                testId="hq-address"
                            />
                            {hq.hq_address_parts?.lat && (
                                <div className="text-[11px] text-[var(--rom-text-2)] flex items-center gap-2 flex-wrap">
                                    <span className="chip chip-green" style={{ padding: "1px 6px" }}>ZIP {hq.hq_address_parts.zip}</span>
                                    <span>{hq.hq_address_parts.city}, {hq.hq_address_parts.state}</span>
                                    <span className="font-mono">{Number(hq.hq_address_parts.lat).toFixed(4)}, {Number(hq.hq_address_parts.lng).toFixed(4)}</span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-sm text-[var(--rom-text-2)]">
                            {hq.hq_address || <span className="text-[var(--rom-text-3)] italic">Not set — ask a manager or admin to configure.</span>}
                        </div>
                    )}
                </div>
            </div>

            <div className="rom-card">
                <div className="rom-card-head"><h3>Franchise branding</h3></div>
                <div className="rom-card-body space-y-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rom-text-3)] font-semibold mb-1">Franchise</div>
                        <div className="font-display font-bold text-xl" data-testid="franchise-name">{FRANCHISE_NAME}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                        <Swatch hex="#FF6A13" label="Orange · primary" />
                        <Swatch hex="#59BA47" label="Green · chrome" />
                        <Swatch hex="#0E1A24" label="Navy · success" />
                    </div>
                </div>
            </div>

            {isAdmin && (
                <div className="rom-card" data-testid="demo-data-card">
                    <div className="rom-card-head">
                        <h3><Database size={14} weight="bold" /> Demo data</h3>
                        {seedResult && <span className="chip chip-green">Loaded</span>}
                    </div>
                    <div className="rom-card-body space-y-3">
                        <p className="text-xs text-[var(--rom-text-2)] leading-relaxed">
                            Populate a demo-ready dataset: <b>3 projects</b> (1 completed water large-loss, 1 fire in-progress, 1 residential in-progress), each loaded with ROMs, P&amp;L revenue-vs-cost, Gantt phases, timesheets, schedule entries, sub-invoices and shared proposals — plus a standalone <b>Commercial Cleaning</b> quote and an adjuster <b>Not-To-Exceed</b> authorization. <b>Additive</b> (never deletes your real data) and safe to re-run.
                        </p>
                        <button className="btn btn-primary" onClick={loadDemoData} disabled={seeding} data-testid="load-demo-data">
                            {seeding ? <CircleNotch size={14} className="animate-spin" /> : <Database size={14} weight="bold" />}
                            {seeding ? "Loading demo data…" : "Load demo data"}
                        </button>
                        {seedResult && (
                            <div className="text-[11px] text-[var(--rom-text-2)] flex flex-wrap gap-x-4 gap-y-1 pt-1" data-testid="demo-seed-result">
                                <span className="flex items-center gap-1 text-[var(--rom-green)]"><CheckCircle size={12} weight="fill" /> Done:</span>
                                {Object.entries(seedResult).map(([k, v]) => (
                                    <span key={k} className="font-mono">{k.replace(/_/g, " ")}: <b>{v}</b></span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                    <div className="rom-card w-full max-w-md">
                        <div className="rom-card-head"><h3>New user</h3><button onClick={() => setOpen(false)}><X size={16} /></button></div>
                        <div className="rom-card-body space-y-3">
                            <Input label="Name" v={draft.name} on={(v) => setDraft({...draft, name: v})} testId="new-user-name" />
                            <Input label="Email" v={draft.email} on={(v) => setDraft({...draft, email: v})} testId="new-user-email" />
                            <Input label="Password" type="password" v={draft.password} on={(v) => setDraft({...draft, password: v})} testId="new-user-password" />
                            <div>
                                <label className="label">Role</label>
                                <select className="select" value={draft.role} onChange={(e) => setDraft({...draft, role: e.target.value})} data-testid="new-user-role">
                                    <option value="estimator">Estimator</option>
                                    <option value="manager">Manager</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={createUser} data-testid="new-user-save">Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Input({ label, v, on, type = "text", testId }) {
    return <div><label className="label">{label}</label><input type={type} className="input" value={v} onChange={(e) => on(e.target.value)} data-testid={testId} /></div>;
}
function EditableUserRow({ u, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(u.name);
    const [busy, setBusy] = useState(false);

    async function save() {
        if (!name.trim() || name === u.name) { setEditing(false); setName(u.name); return; }
        setBusy(true);
        try {
            await endpoints.updateUser(u.id, { name: name.trim() });
            onSaved({ ...u, name: name.trim() });
            setEditing(false);
        } catch (e) { alert(formatApiError(e)); setName(u.name); }
        finally { setBusy(false); }
    }

    return (
        <tr data-testid={`user-row-${u.id}`}>
            <td className="font-medium">
                {editing ? (
                    <div className="flex items-center gap-1.5">
                        <input autoFocus value={name} onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setName(u.name); } }}
                            className="h-7 px-2 rounded-[4px] border border-[var(--rom-orange)] text-sm w-40" data-testid={`user-name-input-${u.id}`} />
                        <button onClick={save} disabled={busy} data-testid={`user-name-save-${u.id}`}>
                            {busy ? <CircleNotch size={13} className="animate-spin" /> : <CheckCircle size={14} weight="bold" className="text-[var(--rom-green)]" />}
                        </button>
                        <button onClick={() => { setEditing(false); setName(u.name); }} data-testid={`user-name-cancel-${u.id}`}><X size={14} className="text-[var(--rom-text-3)]" /></button>
                    </div>
                ) : (
                    <span className="inline-flex items-center gap-1.5 group">
                        {u.name}
                        <button onClick={() => setEditing(true)} className="opacity-40 hover:opacity-100" title="Edit name" data-testid={`user-name-edit-${u.id}`}>
                            <PencilSimple size={12} weight="bold" />
                        </button>
                    </span>
                )}
            </td>
            <td className="font-mono text-xs">{u.email}</td>
            <td><span className={`chip chip-${u.role === "admin" ? "orange" : u.role === "manager" ? "navy" : "green"}`}>{u.role}</span></td>
            <td className="text-xs text-[var(--rom-text-2)]">{u.created_at?.slice(0, 10)}</td>
        </tr>
    );
}
function KV({ k, v }) {
    return <div className="flex justify-between p-2 rounded bg-slate-50 border border-[var(--rom-border)]"><span className="text-[var(--rom-text-2)]">{k}</span><span className="font-mono font-bold">{v}</span></div>;
}
function Swatch({ hex, label }) {
    return (
        <div className="border border-[var(--rom-border)] rounded-[4px] overflow-hidden">
            <div className="h-16" style={{ background: hex }} />
            <div className="p-3"><div className="font-mono text-sm">{hex}</div><div className="text-xs text-[var(--rom-text-2)]">{label}</div></div>
        </div>
    );
}
